import { newerPerson, sortConversations, type MessengerClient } from '@sd/client-core';
import type { FileHit, MessageHit, Person } from '@sd/contracts';
import { useEffect, useId, useState } from 'react';
import { formatBytes, formatClock, formatDayDivider, formatListTime } from '../../format';
import { Icon } from '../../icons/Icon';
import { Avatar } from '../../ui/Avatar';
import { Button, IconButton, Notice, Segmented, Tag } from '../../ui/controls';
import { isComposing } from '../../ui/keys';
import { dismissDialog, Modal, useToast } from '../../ui/overlays';
import { FileIcon, ScanTag } from '../attachments';
import { ConversationAvatar, Highlight } from '../bits';
import { useAppEnv, useClient, useClientState } from '../context';
import { errorMessage } from '../../errors';
import { conversationName } from '../copy';
import { isMacLike, useShell } from '../shell';

type Tab = 'msg' | 'file' | 'people' | 'saved';
type SavedPage = Awaited<ReturnType<MessengerClient['listSaved']>>;
interface Results {
  messages: MessageHit[];
  files: FileHit[];
  people: Person[];
  counts: { messages: number; files: number; people: number };
  limited: { shortQuery: boolean };
}

/** Recent queries are a convenience kept in memory per session, so another account or server never sees them. */
const recentQueries = new WeakMap<MessengerClient, string[]>();

function recentFor(client: MessengerClient): string[] {
  let list = recentQueries.get(client);
  if (!list) {
    list = [];
    recentQueries.set(client, list);
  }
  return list;
}

/** Global / in-conversation search (Ctrl/⌘K; design m-search), and the quick way to another conversation by its name. */
export function SearchModal({ scope: initialScope, tab: initialTab }: { scope?: string | null; tab?: 'saved' }) {
  const env = useAppEnv();
  const shell = useShell();
  const client = useClient();
  const toast = useToast();
  const conversations = useClientState((s) => s.conversations);
  const people = useClientState((s) => s.people);
  const meId = useClientState((s) => s.me?.id ?? '');
  const activeId = useClientState((s) => s.activeConversationId);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Tab>(initialTab ?? 'msg');
  const savedOn = useClientState((s) => s.serverInfo?.capabilities.includes('messages.saved') ?? false);
  const savedIds = useClientState((s) => s.saved);
  const [saved, setSaved] = useState<SavedPage | null>(null);
  const [scope, setScope] = useState<string | null>(initialScope ?? null);
  const [results, setResults] = useState<Results | null>(null);
  const [kb, setKb] = useState(-1);
  const listId = useId();
  const query = q.trim();
  const recent = recentFor(client);

  useEffect(() => {
    if (!query) {
      setResults(null);
      return undefined;
    }
    let alive = true;
    const timer = window.setTimeout(() => {
      client
        .search(query, scope ?? undefined)
        .then((r) => alive && setResults(r))
        .catch((error: unknown) => alive && toast(errorMessage(error)));
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [client, query, scope, toast]);

  // "저장": the newest saved first, read again whenever something is saved or taken out (here or elsewhere).
  useEffect(() => {
    if (tab !== 'saved') return undefined;
    let alive = true;
    client.listSaved().then(
      (page) => alive && setSaved(page),
      (error: unknown) => alive && toast(errorMessage(error)),
    );
    return () => {
      alive = false;
    };
  }, [client, tab, savedIds, toast]);
  const moreSaved = () => {
    if (!saved?.nextBefore) return;
    client.listSaved(saved.nextBefore).then(
      (page) => setSaved((current) => ({ items: [...(current?.items ?? []), ...page.items], nextBefore: page.nextBefore })),
      (error: unknown) => toast(errorMessage(error)),
    );
  };

  const remember = () => {
    if (query && !recent.includes(query)) recent.unshift(query);
    recent.splice(8);
  };
  const openHit = (conversationId: string, messageId: string | null) => {
    remember();
    env.closeModal();
    shell.openConversation(conversationId, messageId ? { messageId } : undefined);
  };
  const openPerson = (id: string) => {
    remember();
    const existing = Object.values(conversations).find((c) => c.kind === 'direct' && c.peer?.id === id);
    if (existing) {
      env.closeModal();
      shell.openConversation(existing.id);
    } else env.openModal({ kind: 'people', mode: 'new', preselect: [id] });
  };

  const counts = results?.counts;
  const items: { key: string; node: React.ReactNode; go(): void }[] = [];
  // Ctrl/⌘K, a few letters of the name, Enter: conversations come first. Before anything is typed, the latest others.
  const needle = query.toLocaleLowerCase('ko');
  const rooms =
    scope || tab !== 'msg'
      ? []
      : sortConversations(Object.values(conversations))
          .filter((c) => (needle ? conversationName(c).toLocaleLowerCase('ko').includes(needle) : c.id !== activeId))
          .slice(0, needle ? 5 : 6);
  for (const c of rooms) {
    items.push({
      key: `conversation:${c.id}`,
      go: () => {
        if (query) remember();
        env.closeModal();
        shell.openConversation(c.id);
      },
      node: (
        <>
          <ConversationAvatar conversation={c} />
          <span className="sr-main">
            <span className="sr-top">
              <b className="ell">
                <Highlight text={conversationName(c)} q={query} />
              </b>
              <Tag>{c.kind === 'direct' ? '1:1 대화' : `그룹 ${c.memberCount}명`}</Tag>
              {c.unreadCount ? <span style={{ marginLeft: 'auto', flex: 'none' }}>안 읽은 메시지 {c.unreadCount}개</span> : null}
            </span>
            <span className="sr-txt">대화로 이동</span>
          </span>
        </>
      ),
    });
  }
  if (tab === 'saved' && saved) {
    for (const { message, savedAt } of saved.items) {
      if (!savedIds[message.id]) continue;
      const where = conversations[message.conversationId];
      const text = message.text ?? message.attachments.map((f) => f.name).join(', ');
      if (needle && !`${text} ${where ? conversationName(where) : ''}`.toLocaleLowerCase('ko').includes(needle)) continue;
      const sender = message.senderId ? people[message.senderId] : undefined;
      items.push({
        key: `saved:${message.id}`,
        go: () => openHit(message.conversationId, message.id),
        node: (
          <>
            <Avatar person={sender} noDot />
            <span className="sr-main">
              <span className="sr-top">
                <b>{message.senderId === meId ? '나' : (sender?.displayName ?? '')}</b>
                <span className="ell">{where ? conversationName(where) : ''}</span>
                <span style={{ marginLeft: 'auto', flex: 'none' }}>{formatListTime(savedAt, env.now())} 저장</span>
              </span>
              <span className="sr-txt">
                <Highlight text={text} q={query} />
              </span>
            </span>
          </>
        ),
      });
    }
  }
  if (results && tab === 'msg') {
    for (const hit of results.messages) {
      const sender = hit.senderId ? people[hit.senderId] : undefined;
      items.push({
        key: hit.messageId,
        go: () => openHit(hit.conversationId, hit.messageId),
        node: (
          <>
            <Avatar person={sender} noDot />
            <span className="sr-main">
              <span className="sr-top">
                <b>{hit.senderId === meId ? '나' : (sender?.displayName ?? '')}</b>
                <span className="ell">{hit.conversationTitle}</span>
                <span style={{ marginLeft: 'auto', flex: 'none' }}>
                  {formatDayDivider(hit.createdAt, env.now())} {formatClock(hit.createdAt)}
                </span>
              </span>
              <span className="sr-txt">
                <Highlight text={hit.text} q={query} />
              </span>
            </span>
          </>
        ),
      });
    }
  }
  if (results && tab === 'file') {
    for (const hit of results.files) {
      items.push({
        key: hit.file.id,
        go: () => openHit(hit.conversationId, hit.file.messageId),
        node: (
          <>
            <FileIcon ext={hit.file.ext} style={{ width: 38, height: 44, borderRadius: 11, fontSize: 9 }} />
            <span className="sr-main">
              <span className="sr-top">
                <b className="ell">
                  <Highlight text={hit.file.name} q={query} />
                </b>
              </span>
              <span className="sr-txt">
                {hit.conversationTitle} · {formatBytes(hit.file.size)} · {formatListTime(hit.file.createdAt, env.now())} · <ScanTag file={hit.file} />
              </span>
            </span>
          </>
        ),
      });
    }
  }
  if (results && tab === 'people') {
    for (const hit of results.people) {
      // The directory follows changes as they happen; a result shows a person as they were when it was found.
      const known = people[hit.id];
      const p = known ? newerPerson(hit, known) : hit;
      items.push({
        key: p.id,
        go: () => openPerson(p.id),
        node: (
          <>
            <Avatar person={p} />
            <span className="sr-main">
              <span className="sr-top">
                <b>
                  <Highlight text={p.displayName} q={query} />
                </b>
                {p.status === 'deactivated' ? <Tag>비활성화</Tag> : null}
              </span>
              <span className="sr-txt">
                <Highlight text={p.department} q={query} /> · {p.email}
              </span>
            </span>
          </>
        ),
      });
    }
  }

  const tabLabel = (label: string, n: number | undefined) => (query && n !== undefined ? `${label} ${n}` : label);
  const scoped = scope ? conversations[scope] : undefined;
  // Combobox with a listbox popup (WAI-ARIA): focus stays in the input while ↑/↓ pick a result.
  const listed = items.length > 0;
  return (
    <Modal open onClose={env.closeModal} labelledBy="ms-t" id="m-search" size="wide">
      <div className="dlg-head">
        <h2 id="ms-t">검색</h2>
        <span className="kbd">{isMacLike() ? '⌘K' : 'Ctrl K'}</span>
        <IconButton icon="x" label="닫기" onClick={() => dismissDialog(env.closeModal)} />
      </div>
      <div className="dlg-body scroll">
        <label className="s-input">
          <Icon name="search" />
          <input
            placeholder="대화 이름, 메시지, 파일, 사람 검색"
            aria-label="검색어"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listed}
            aria-controls={listId}
            aria-activedescendant={listed && kb >= 0 ? `${listId}-${kb}` : undefined}
            value={q}
            data-autofocus
            onChange={(e) => {
              setQ(e.target.value);
              setKb(-1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setKb((k) => Math.min(items.length - 1, k + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setKb((k) => Math.max(0, k - 1));
              } else if (e.key === 'Enter' && !isComposing(e)) {
                e.preventDefault();
                items[Math.max(0, kb)]?.go();
              }
            }}
          />
        </label>
        <div>
          <Segmented
            mode="tabs"
            label="검색 대상"
            value={tab}
            onChange={(v) => {
              setTab(v);
              setKb(-1);
            }}
            options={[
              { value: 'msg', label: tabLabel('메시지', counts?.messages) },
              { value: 'file', label: tabLabel('파일', counts?.files) },
              { value: 'people', label: tabLabel('사람', counts?.people) },
              ...(savedOn && !scope ? [{ value: 'saved' as const, label: '저장' }] : []),
            ]}
          />
          {scoped ? (
            <>
              {' '}
              <span className="tag blue" style={{ marginLeft: 6 }}>
                이 대화에서: {conversationName(scoped)}{' '}
                <button type="button" onClick={() => setScope(null)} aria-label="범위 해제" style={{ display: 'inline-grid' }}>
                  <Icon name="x" size="xs" />
                </button>
              </span>
            </>
          ) : null}
        </div>
        {query && [...query].length <= 2 ? (
          <div style={{ marginTop: 10 }}>
            <Notice tone="neutral">짧은 검색어는 최근 30일 · 최대 50개 결과로 제한돼요</Notice>
          </div>
        ) : null}
        {!query && recent.length && tab !== 'saved' ? (
          <>
            <div className="sec-h">최근 검색어</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 6px' }}>
              {recent.map((w) => (
                <button key={w} type="button" className="chip" onClick={() => setQ(w)}>
                  {w}
                </button>
              ))}
            </div>
          </>
        ) : null}
        {!query && items.length && tab !== 'saved' ? <div className="sec-h">최근 대화</div> : null}
        <div
          className="s-res"
          id={listId}
          role={listed ? 'listbox' : undefined}
          aria-label={listed ? (tab === 'saved' ? '저장한 메시지' : query ? '검색 결과' : '최근 대화') : undefined}
        >
          {tab === 'saved' && saved && !items.length ? (
            <div className="empty-note">{query ? `‘${query}’ 저장한 메시지가 없어요` : '저장한 메시지가 없어요. 메시지 메뉴의 ‘저장’으로 모아 둘 수 있어요'}</div>
          ) : query && results && !items.length && tab !== 'saved' ? (
            <div className="empty-note">‘{query}’ 검색 결과가 없어요</div>
          ) : (
            items.map((item, i) => (
              <button
                key={item.key}
                id={`${listId}-${i}`}
                type="button"
                role="option"
                aria-selected={i === kb}
                className={`sr-item${i === kb ? ' kb' : ''}`}
                onClick={item.go}
                ref={(el) => (i === kb ? el?.scrollIntoView({ block: 'nearest' }) : undefined)}
              >
                {item.node}
              </button>
            ))
          )}
        </div>
        {tab === 'saved' && saved?.nextBefore ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            <Button size="sm" variant="ghost" onClick={moreSaved}>
              더 보기
            </Button>
          </div>
        ) : null}
        {tab === 'saved' ? (
          <p className="hint" style={{ marginTop: 14 }}>
            누르면 원래 위치로 이동해요. 메시지 메뉴의 ‘저장 취소’로 뺄 수 있어요. 나간 대화의 메시지나 삭제된 메시지는 보이지 않아요.
          </p>
        ) : !query ? (
          <p className="hint" style={{ marginTop: 14 }}>
            대화 이름을 몇 글자 넣고 Enter를 누르면 그 대화로 바로 가요. 메시지·파일은 내가 참여한 대화와 열람 가능한 범위에서만 검색돼요. 결과를 누르면 원래 위치로 이동해요.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
